import type { SupportedLocale } from '../../i18n/languages';

/** Explicit user preference or resolved calendar order. */
export type DateFormatOrder = 'MDY' | 'DMY' | 'YMD';

/** Stored preference: null means auto from uiLanguage. */
export type DateFormatPreference = DateFormatOrder | null;

const DMY_LOCALES = new Set<string>(['pt-BR', 'es-MX', 'fr-CA']);
const YMD_LOCALES = new Set<string>(['zh-CN']);

/**
 * Resolve effective date order from optional explicit preference and UI language.
 * When preference is null/undefined, derive from language.
 */
export function resolveDateFormat(
    uiLanguage?: string | null,
    dateFormat?: DateFormatPreference | undefined,
): DateFormatOrder {
    if (dateFormat === 'MDY' || dateFormat === 'DMY' || dateFormat === 'YMD') {
        return dateFormat;
    }
    const lang = String(uiLanguage || 'en-US').trim();
    if (YMD_LOCALES.has(lang)) return 'YMD';
    if (DMY_LOCALES.has(lang)) return 'DMY';
    return 'MDY';
}

/** Human-readable pattern for hints and pickers (slash separators). */
export function dateFormatPattern(order: DateFormatOrder): string {
    switch (order) {
        case 'DMY':
            return 'dd/MM/yyyy';
        case 'YMD':
            return 'yyyy-MM-dd';
        case 'MDY':
        default:
            return 'MM/dd/yyyy';
    }
}

/** react-datepicker `dateFormat` token string. */
export function pickerDateFormat(order: DateFormatOrder): string {
    return dateFormatPattern(order);
}

/** date-fns format token for numeric short dates. */
export function dateFnsShortPattern(order: DateFormatOrder): string {
    switch (order) {
        case 'DMY':
            return 'dd/MM/yyyy';
        case 'YMD':
            return 'yyyy-MM-dd';
        case 'MDY':
        default:
            return 'MM/dd/yyyy';
    }
}

/**
 * Compact abbreviated-month patterns for table/list display.
 * Avoids verbose Intl phrasing (e.g. pt-BR “30 de jul. de 2026”).
 */
export function dateFnsMediumPattern(order: DateFormatOrder, includeYear: boolean): string {
    switch (order) {
        case 'DMY':
            return includeYear ? 'd MMM yyyy' : 'd MMM';
        case 'YMD':
            return includeYear ? 'yyyy MMM d' : 'MMM d';
        case 'MDY':
        default:
            return includeYear ? 'MMM d, yyyy' : 'MMM d';
    }
}

/** Labels for preference picker. */
export const DATE_FORMAT_OPTION_LABELS: Record<DateFormatOrder, string> = {
    MDY: 'MM/DD/YYYY',
    DMY: 'DD/MM/YYYY',
    YMD: 'YYYY-MM-DD',
};

export function defaultOrderForLocale(locale: SupportedLocale | string): DateFormatOrder {
    return resolveDateFormat(locale, null);
}
